import EntityListShell from "./EntityListShell";
import { TextField, RepeatableStrings, TagsField, deText } from "./fields";
import { translateFields } from "@/lib/adminContentApi";

const TYPE_OPTIONS = [
  { value: "freelance", label: "Freelance" },
  { value: "permanent", label: "Festanstellung" },
  { value: "student", label: "Werkstudent" },
  { value: "temp", label: "Zeitarbeit" },
  { value: "academic", label: "Akademisch" },
  { value: "leave", label: "Auszeit" },
];

function emptyForm() {
  return { period: "", type: "freelance", company: "", role: "", client: "", location: "", highlights: [""], tech: [] };
}

function toFormState(item) {
  return {
    period: item.period || "",
    type: item.type || "freelance",
    company: deText(item.company),
    role: deText(item.role),
    client: deText(item.client),
    location: deText(item.location),
    highlights: (item.highlights || []).map(deText),
    tech: item.tech || [],
  };
}

async function buildPayload(form) {
  const texts = { company: form.company, role: form.role, location: form.location };
  if (form.client.trim()) texts.client = form.client;
  form.highlights.forEach((h, i) => {
    if (h.trim()) texts[`highlight_${i}`] = h;
  });
  const { en = {}, fr = {} } = await translateFields(texts);
  const ml = (key, fallback) => ({ de: fallback, en: en[key] || fallback, fr: fr[key] || fallback });
  return {
    period: form.period,
    type: form.type,
    company: ml("company", form.company),
    role: ml("role", form.role),
    location: ml("location", form.location),
    ...(form.client.trim() ? { client: ml("client", form.client) } : {}),
    highlights: form.highlights.map((h, i) => (h.trim() ? ml(`highlight_${i}`, h) : null)).filter(Boolean),
    tech: form.tech,
  };
}

export default function ExperienceEditor() {
  return (
    <EntityListShell
      collection="experience"
      title="Erfahrung"
      description="Deine beruflichen Stationen. Reihenfolge per Pfeiltasten änderbar."
      newLabel="Station hinzufügen"
      testidBase="exp-admin"
      emptyForm={emptyForm}
      toFormState={toFormState}
      buildPayload={buildPayload}
      renderRowSummary={(item) => (
        <div>
          <div className="font-mono text-xs text-[var(--gold)]">{item.period}</div>
          <div className="text-[var(--text-0)] font-display">{deText(item.role)}</div>
          <div className="text-xs text-[var(--text-2)]">{deText(item.company)}</div>
        </div>
      )}
      renderFields={(form, setForm) => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Zeitraum"
              testid="exp-period"
              value={form.period}
              onChange={(v) => setForm({ ...form, period: v })}
              placeholder="z. B. 02/2025 – 05/2026"
              required
            />
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">Typ</span>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="exp-type"
                className="mt-2 w-full bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-2 text-[var(--text-0)] text-sm"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <TextField label="Unternehmen" testid="exp-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} required />
          <TextField label="Rolle" testid="exp-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} required />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Kunde (optional)" testid="exp-client" value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
            <TextField label="Ort" testid="exp-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
          </div>
          <RepeatableStrings
            label="Aufgaben & Erfolge"
            testid="exp-highlight"
            items={form.highlights}
            onChange={(v) => setForm({ ...form, highlights: v })}
            placeholder="z. B. Betreuung von 2 Entwicklungsteams"
          />
          <TagsField label="Tech-Stack" testid="exp-tech" value={form.tech} onChange={(v) => setForm({ ...form, tech: v })} placeholder="Java, Spring Boot, AWS" />
        </div>
      )}
    />
  );
}
